import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { MenuService } from '../menu/menu.service';

/**
 * MenuGuard — prevents direct URL navigation to routes not in the user's menu.
 *
 * This is the Angular-side UX protection layer.
 * The real security gate is the backend API (three-gate filter in MenuService.java).
 *
 * If the menu has not loaded yet, waits for the first load then checks.
 * If the route is not in allowedRoutes$, redirects to /dashboard.
 *
 * Add to app.routes.ts:
 *   canActivate: [AuthGuard, MenuGuard]
 *
 * Note: AuthGuard must come BEFORE MenuGuard in the array so unauthenticated
 * users are redirected to login before MenuGuard tries to check the menu.
 */
@Injectable({ providedIn: 'root' })
export class MenuGuard implements CanActivate {

  constructor(
    private menuService: MenuService,
    private router: Router
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {

    const requestedRoute = '/' + state.url.split('?')[0].split('/').filter(Boolean)[0] || '/dashboard';

    return this.menuService.loaded$.pipe(
      // Wait until the menu has loaded (avoids false negatives during page load)
      switchMap(loaded => {
        if (!loaded) {
          // Menu not loaded yet — trigger a load and allow navigation for now
          // The guard will be called again on the next navigation after load
          this.menuService.loadMenu();
          return of(true);
        }
        return of(this.menuService.isRouteAllowed(requestedRoute));
      }),
      take(1),
      map(allowed => {
        if (!allowed) {
          console.warn('[MenuGuard] Route not allowed:', requestedRoute, '— redirecting to dashboard');
          this.router.navigate(['/dashboard']);
          return false;
        }
        return true;
      })
    );
  }
}
